/**
 * Materialize monthly cost / sales schedule shapes from cash events.
 * Events are the timing SoT; these arrays exist so buildLedger and strategies
 * can keep their month-loop roll-up without a rewrite.
 */

import { hydrateAssumptions, normalizeSalesAllocation } from './defaults.js';
import { n } from './math.js';
import { buildSalesPhaseTargets } from './salesPhases.js';

const COST_CATEGORIES = [
  'landPrice',
  'acquisitionCosts',
  'upfrontFees',
  'feasibilityCosts',
  'professionalSoftCosts',
  'constructionCost',
  'constructionReserve',
  'commercialCosts',
  'financingCosts',
  'taxes',
];

function zeroSeries(totalMonths) {
  return Array(Math.max(0, totalMonths)).fill(0);
}

/**
 * @returns {{
 *   byCategory: Record<string, number[]>,
 *   totalByMonth: number[],
 *   totals: Record<string, number>,
 *   categories: string[],
 * }}
 */
export function materializeCostSchedule(events, programme) {
  const T = Math.max(1, programme.totalMonths || 1);
  const byCategory = {};
  for (const key of COST_CATEGORIES) byCategory[key] = zeroSeries(T);

  for (const ev of events || []) {
    if (ev.direction !== 'out') continue;
    const category = ev.category;
    if (!category || !byCategory[category]) continue;
    const idx = Math.min(T, Math.max(1, ev.month)) - 1;
    byCategory[category][idx] += n(ev.amount);
  }

  const totals = {};
  const totalByMonth = zeroSeries(T);
  for (const key of COST_CATEGORIES) {
    totals[key] = byCategory[key].reduce((s, v) => s + v, 0);
    for (let i = 0; i < T; i += 1) totalByMonth[i] += byCategory[key][i];
  }

  return {
    byCategory,
    totalByMonth,
    totals,
    categories: [...COST_CATEGORIES],
  };
}

/**
 * Build sales schedule months from sale events (step cumulative %).
 */
export function materializeSalesSchedule(events, pnl, programme, rawAssumptions = {}) {
  const a = hydrateAssumptions(rawAssumptions);
  const allocation = normalizeSalesAllocation(a.salesAllocation);
  const phaseTiming = { ...a.salesPhaseTiming };
  const totalRevenue = Math.max(0, n(pnl.salesRevenue));
  const totalUnits = Math.max(0, n(pnl.units));
  const totalMonths = Math.max(1, programme.totalMonths);
  const auth = Math.max(1, programme.permitMonth || programme.authorizationEnd || 1);
  const phaseTargets = buildSalesPhaseTargets(programme, allocation, phaseTiming) || [];

  const saleEvents = (events || [])
    .filter((e) => e.kind === 'sale')
    .slice()
    .sort((a, b) => a.month - b.month || String(a.id).localeCompare(String(b.id)));

  const deltas = Array(totalMonths).fill(0);
  if (totalRevenue > 0) {
    for (const sale of saleEvents) {
      const idx = Math.min(totalMonths, Math.max(1, sale.month)) - 1;
      deltas[idx] += n(sale.amount) / totalRevenue;
    }
  } else if (saleEvents.length) {
    const each = 1 / saleEvents.length;
    for (const sale of saleEvents) {
      const idx = Math.min(totalMonths, Math.max(1, sale.month)) - 1;
      deltas[idx] += each;
    }
  }

  // Events are the SoT — never invent a silent residual on delivery.
  // Only absorb tiny float residue into the last sale-event month.
  const deltaSum = deltas.reduce((s, v) => s + v, 0);
  if (saleEvents.length && Math.abs(deltaSum - 1) > 1e-12 && Math.abs(deltaSum - 1) <= 1e-6) {
    const lastSaleMonth = saleEvents[saleEvents.length - 1].month;
    const idx = Math.min(totalMonths, Math.max(1, lastSaleMonth)) - 1;
    deltas[idx] += 1 - deltaSum;
  }

  const months = [];
  let cum = 0;
  for (let month = 1; month <= totalMonths; month += 1) {
    const salesDelta = Math.max(0, deltas[month - 1] || 0);
    cum = Math.min(1, cum + salesDelta);
    // Do not force cum → 1 on the final month; incomplete cover stays visible
    // unless resolveSaleEvents already added an explicit residual event.
    const hit = saleEvents.find((e) => e.month === month);
    months.push({
      month,
      salesPct: cum,
      salesDelta,
      revenueSold: totalRevenue * cum,
      revenueDelta: totalRevenue * salesDelta,
      unitsSold: totalUnits * cum,
      unitsDelta: totalUnits * salesDelta,
      phase: hit?.meta?.phase
        || phaseTargets.find((t) => month <= t.month)?.phase
        || 'late',
    });
  }

  const phaseAmounts = { preBank: 0, postBank: 0, late: 0 };
  if (phaseTargets.length >= 2) {
    for (const row of months) {
      const preEnd = phaseTargets[0].month;
      const postEnd = phaseTargets[1].month;
      if (row.month >= auth && row.month <= preEnd) phaseAmounts.preBank += row.salesDelta;
      else if (row.month <= postEnd) phaseAmounts.postBank += row.salesDelta;
      else phaseAmounts.late += row.salesDelta;
    }
  }

  // Prefer explicit sale-event phase tags when present (assumption mode).
  const tagged = { preBank: 0, postBank: 0, late: 0 };
  let taggedSum = 0;
  for (const sale of saleEvents) {
    const phase = sale.meta?.phase;
    if (phase && tagged[phase] != null) {
      tagged[phase] += totalRevenue > 0 ? n(sale.amount) / totalRevenue : 0;
      taggedSum += totalRevenue > 0 ? n(sale.amount) / totalRevenue : 0;
    }
  }
  const resolvedPhaseAmounts = taggedSum > 0.5 ? tagged : phaseAmounts;

  return {
    months,
    allocation,
    phaseTiming,
    phaseTargets,
    phaseAmounts: resolvedPhaseAmounts,
    allocationAmounts: {
      preBank: totalRevenue * allocation.preBank,
      postBank: totalRevenue * allocation.postBank,
      late: totalRevenue * allocation.late,
    },
    saleEvents,
  };
}
